import { computed, ref } from 'vue';

import useScrollLock from './ui/useScrollLock';
import { onMenuOpen, onMenuClose } from '#shared/features/analytics/mobileMenuTags';
import { tagAccountDrawerOpen, tagAccountDrawerClose } from '#shared/features/analytics/accountTags';
import useProvideFactory from './dumb/useProvideFactory';
import { MOBILE_NAVIGATION_OVERLAY } from '#shared/constants/common/overlayNames';
import { RESET_OVERLAY, SET_ACTIVE_OVERLAY_NAME } from '#shared/types/actions';

export const MOBILE_NAV_REPOSITORY = Symbol('MOBILE_NAV_REPOSITORY');

export function getMobileNavSharedState() {
  const mode = ref('root');
  const eventClass = ref('');
  const stack = ref([]);
  const poss = ref([]);
  const isMenuOpened = ref(false);
  const isMenuHydrated = ref(false);
  const wasSideNavVisibleRecently = ref(false);
  const menuTarget = ref(null);

  return {
    mode,
    eventClass,
    stack,
    poss,
    isMenuOpened,
    isMenuHydrated,
    wasSideNavVisibleRecently,
    menuTarget,
  };
}

export function useMobileNavProvide(useAsInject) {
  return useProvideFactory({
    key: MOBILE_NAV_REPOSITORY,
    factory: getMobileNavSharedState,
    useAsInject,
  });
}

export function useMobileNav() {
  const navOverlay = useNavOverlay();
  const { mode, eventClass, stack, poss, isMenuOpened, isMenuHydrated, wasSideNavVisibleRecently, menuTarget } = useMobileNavProvide(true);
  const { lock, unlock } = useScrollLock();

  function getLinkPosition() {
    return poss.value[poss.value.length - 1];
  }

  function lastStackItem() {
    return stack.value[stack.value.length - 1];
  }

  function back() {
    eventClass.value = 'collapse';
    stack.value.pop();
    poss.value.pop();

    if (!stack.value.length) {
      mode.value = 'root';
    }
  }

  function reset() {
    stack.value = [];
    poss.value = [];
    mode.value = 'root';
    eventClass.value = '';
  }

  function setMode({ categoryId, analyticsCategoryId, id, pos, title, url, textTransform, animate = true }, isSub) {
    const newId = categoryId || id;
    eventClass.value = animate ? 'expand' : '';
    mode.value = newId;

    if (isSub) {
      stack.value.push({
        title,
        url,
        id: newId,
        analyticsCategoryId,
        textTransform,
      });
      poss.value.push(pos);
    }
  }

  function stackLen() {
    return stack.value.length;
  }

  function openNav(params = {}) {
    const { isAccountSubcategoryOpened } = params;

    lock();
    isMenuOpened.value = true;
    wasSideNavVisibleRecently.value = true;

    if (isAccountSubcategoryOpened) {
      tagAccountDrawerOpen('hamburger drawer');
    } else {
      onMenuOpen();
    }

    navOverlay[SET_ACTIVE_OVERLAY_NAME](MOBILE_NAVIGATION_OVERLAY);
  }

  function closeNav() {
    unlock();
    isMenuOpened.value = false;
    const isAccountSubcategoryOpened = lastStackItem()?.id === 'accountContent';

    if (isAccountSubcategoryOpened) {
      tagAccountDrawerClose();
    } else {
      onMenuClose();
    }
    reset();
    navOverlay[RESET_OVERLAY]();
    menuTarget.value?.focus();

    // note: this setTimeout() helps us keep track if certain actions were
    // perform while the side nav was open. The short 1sec delay is just
    // a window to check for that validation.
    setTimeout(() => {
      wasSideNavVisibleRecently.value = false;
    }, 1000);
  }

  function toggleNav() {
    if (isMenuOpened.value) {
      closeNav();
    } else {
      menuTarget.value?.blur();
      openNav();
    }
  }

  const isRootMode = computed(() => mode.value === 'root');
  const isStoreMode = computed(() => mode.value === 'store');

  return {
    setMode,
    lastStackItem,
    isRootMode,
    isStoreMode,
    eventClass,
    stack,
    isMenuOpened,
    isMenuHydrated,
    wasSideNavVisibleRecently,
    menuTarget,
    back,
    reset,
    openNav,
    closeNav,
    toggleNav,
    poss,
    mode,
    analytics: {
      getLinkPosition,
      getCol: stackLen,
      getCategoryId: () => lastStackItem()?.analyticsCategoryId,
    },
  };
}

export default {};
